import type {ObjectId} from 'bson';

export type {ReviewActionType} from './reviewActionTypes';

type StringifyDomainObject<T> = {
  [K in keyof T]: T[K] extends ObjectId | Date
    ? string
    : T[K] extends object
      ? StringifyDomainObject<T[K]>
      : T[K];
};

/**
 * Review
 */
export type Review = StringifyDomainObject<ReviewDomain>;

export type ReviewDomain = {
  id: ObjectId;
  siteId: ObjectId;
  type: ReviewTypeType;
  targetId: ObjectId;
  requesterId: ObjectId;
  reviewers: ReviewerDomain[];
  status: ReviewStatusType;
  requirements: RequirementsType;
  createdOn: Date;
  updatedOn: Date;
};

export type Reviewer = StringifyDomainObject<ReviewerDomain>;

export type ReviewerDomain = {
  userId: ObjectId;
  required: boolean;
  requestSentAt?: Date | null;
  reviewerStatus: ReviewerStatusType;
  reviewerStatusUpdatedAt?: Date;
};

export type RequirementsType = {
  numApprovals?: number;
  allRequiredApprovals?: boolean;
};

export type CreateReviewRequirementsType = Omit<
  RequirementsType,
  'numApprovals'
>;

export const REVIEW_TYPES = {
  branch: 'branch',
} as const;
export type ReviewTypeType = keyof typeof REVIEW_TYPES;
export const REVIEW_TYPE_KEYS = Object.keys(REVIEW_TYPES) as ReviewTypeType[];

export const REVIEWER_STATUSES = {
  review_requested: 'review_requested',
  requested_changes: 'requested_changes',
  approved: 'approved',
} as const;
export type ReviewerStatusType = keyof typeof REVIEWER_STATUSES;
export const REVIEWER_STATUS_KEYS = Object.keys(
  REVIEWER_STATUSES
) as ReviewerStatusType[];

export const REVIEW_STATUSES = {
  in_review: 'in_review',
  changes_requested: 'changes_requested',
  approved: 'approved',
  merged: 'merged',
  canceled: 'canceled',
  abandoned: 'abandoned',
} as const;
export type ReviewStatusType = keyof typeof REVIEW_STATUSES;
export const REVIEW_STATUS_KEYS = Object.keys(
  REVIEW_STATUSES
) as ReviewStatusType[];
export const REVIEW_ACTIVE_STATUSES = [
  REVIEW_STATUSES.in_review,
  REVIEW_STATUSES.changes_requested,
  REVIEW_STATUSES.approved,
] as const;
export const REVIEW_TERMINAL_STATUSES = [
  REVIEW_STATUSES.merged,
  REVIEW_STATUSES.canceled,
  REVIEW_STATUSES.abandoned,
] as const;

export type ReviewStatusLabel =
  | 'Branched'
  | 'Pending review'
  | 'Approved'
  | 'Needs changes';

export type ReviewStatusMenuOptionLabel =
  | 'Open review'
  | 'Request review'
  | 'Merge'
  | 'Merging...'
  | 'Check for updates'
  | 'Updating...'
  | 'Delete branch';

// Util for array string type and presence checking
export type ReviewActiveStatusType = (typeof REVIEW_ACTIVE_STATUSES)[number];
export function isReviewActiveStatus(
  status: string
): status is ReviewActiveStatusType {
  return (
    typeof status === 'string' &&
    REVIEW_ACTIVE_STATUSES.includes(status as ReviewActiveStatusType)
  );
}

/**
 * Review events
 */

interface ReviewEventDomainBase {
  id: ObjectId;
  siteId: ObjectId;
  reviewId: ObjectId;
  userId: ObjectId;
  type: ReviewEventTypeType;
  createdOn: Date;
}

export interface ReviewerEventDomain extends ReviewEventDomainBase {
  type: typeof REVIEW_EVENT_TYPES.reviewer_event;
  reviewerEvent: ReviewerStatusType;
  message?: string;
}

export interface BranchEventDomain extends ReviewEventDomainBase {
  type: typeof REVIEW_EVENT_TYPES.branch_event;
  branchEvent: BranchEventTypeType;
  branchId: ObjectId;
  branchName: string;
}

export type ReviewEventDomain = ReviewerEventDomain | BranchEventDomain;

export type ReviewerEvent = StringifyDomainObject<ReviewerEventDomain>;
export type BranchEvent = StringifyDomainObject<BranchEventDomain>;
type ReviewEvent = StringifyDomainObject<ReviewEventDomain>;
export type ReviewEvents = ReviewEvent[];

export const REVIEW_EVENT_TYPES = {
  reviewer_event: 'reviewer_event',
  branch_event: 'branch_event',
} as const;
export type ReviewEventTypeType = keyof typeof REVIEW_EVENT_TYPES;
export const REVIEW_EVENT_TYPE_KEYS = Object.keys(
  REVIEW_EVENT_TYPES
) as ReviewEventTypeType[];

export const BRANCH_EVENT_TYPES = {
  branch_created: 'branch_created',
  branch_merged: 'branch_merged',
  branch_deleted: 'branch_deleted',
};
export type BranchEventTypeType = keyof typeof BRANCH_EVENT_TYPES;
export const BRANCH_EVENT_TYPE_KEYS = Object.keys(
  BRANCH_EVENT_TYPES
) as BranchEventTypeType[];

/**
 * Review notifications
 */
export const REVIEW_ACTIONS = {
  review_created: 'review_created',
  review_approved: 'review_approved',
  review_requested_changes: 'review_requested_changes',
  review_canceled: 'review_canceled',
  review_rerequested: 'review_rerequested',
} as const;
export type ReviewActionsType = keyof typeof REVIEW_ACTIONS;

export const PREFERENCE_LEVELS = {
  all: 'all',
  none: 'none',
} as const;
export type PreferenceLevelsType = keyof typeof PREFERENCE_LEVELS;
export const PREFERENCE_LEVELS_KEYS = Object.keys(
  PREFERENCE_LEVELS
) as PreferenceLevelsType[];

/**
 * Flux store types
 */

export type ReviewsType = Record<string, Review>;

export type ReviewRequestType = {
  branchId: string;
  reviewerIds: string[];
  optionalReviewerIds: string[];
  message?: string;
  branchStagingUrl: string;
  siteShortName: string;
  type: ReviewTypeType;
  requirements?: RequirementsType;
};

export type ReviewApproveType = {
  reviewId: string;
  siteShortName: string;
  branchStagingUrl: string;
  message?: string;
};

export type ReviewRequestChangesType = {
  reviewId: string;
  siteShortName: string;
  branchStagingUrl: string;
  message?: string;
};

export type ReviewCancelType = {
  reviewId: string;
  siteShortName: string;
};

export type ResendReviewRequestType = {
  reviewId: string;
  reviewerId: string;
  siteShortName: string;
};

// Set when another user cancels the active review and this client has the
// review modal open, so the modal can attribute the action to the canceler.
export type ReviewCanceledRemotelyNotice = {
  targetId: string; // branchId of the canceled review's target
  canceledBy: string; // user id of the team member who canceled
};

// Set when another user submits a review request while this client has the
// request-review modal open, so the modal can attribute the action to the
// requester before the in-progress request modal is closed.
export type ReviewRequestedRemotelyNotice = {
  targetId: string; // branchId of the requested review's target
  requestedBy: string; // user id of the team member who submitted the request
};

export type ReviewStateType = {
  reviews: ReviewsType;
  isReviewsLoading: boolean; // Updates when all reviews are being fetched
  reviewsError: null | string; // Error message when fetching reviews failed
  isReviewersLoading: boolean; // Updates when all reviewers are being fetched
  isReviewStatusLoading: boolean; // Updates when a review or approval has been requested/succeeded/failed
  reviewStatusError: null | string; // Error message when a review or approval request failed
  isRequestReviewModalOpen: boolean;
  isReviewModalOpen: boolean;
  isPublishNeeded: boolean;
  isPageSwitchNeeded: boolean; // Indicates when a review action requires a page switch
  reviewCanceledRemotelyNotice: ReviewCanceledRemotelyNotice | null;
  reviewRequestedRemotelyNotice: ReviewRequestedRemotelyNotice | null;
};

export type UnsubscribeReviewParams = {
  siteSlug: string;
  reviewId: string;
  email: string;
  time: string;
  token: string;
};

export interface EligibleReviewer {
  id: string;
  name: string;
}

export interface EligibleReviewers {
  required: EligibleReviewer[];
  optional: EligibleReviewer[];
}
